import React, { FC, useMemo } from 'react'
import { Flex, Tag, Typography } from 'antd'
import type { STRFilters } from './STRFilterBar'

interface ActiveFilter {
  key: keyof STRFilters | string
  label: string
  clearKeys: (keyof STRFilters)[]
}

const COMPARISON_LABELS: Record<string, string> = {
  gte: 'Larger than',
  lte: 'Smaller than',
}

const HOST_PROFILE_LABELS: Record<string, string> = {
  equal: 'Host is the owner',
  not_equal: 'Host is not the owner',
}

interface STRActiveFiltersProps {
  filters: STRFilters
  searchValue: string
  onFiltersChange: (filters: STRFilters) => void
  onSearch: (value: string, column: string) => void
}

const STRActiveFilters: FC<STRActiveFiltersProps> = ({ filters, searchValue, onFiltersChange, onSearch }) => {
  const chips = useMemo(() => {
    const result: ActiveFilter[] = []

    if (filters.yearRange?.[0] && filters.yearRange?.[1]) {
      const start = (filters.yearRange[0] as { year: () => number }).year?.()
      const end = (filters.yearRange[1] as { year: () => number }).year?.()
      if (start && end) {
        result.push({ key: 'yearRange', label: `Year: ${start} - ${end}`, clearKeys: ['yearRange'] })
      }
    }

    if (filters.unreportedRevenueVal != null) {
      const op = COMPARISON_LABELS[filters.unreportedRevenueOp || 'gte']
      result.push({
        key: 'unreportedRevenue',
        label: `Unreported Revenue ${op} $${filters.unreportedRevenueVal}`,
        clearKeys: ['unreportedRevenueOp', 'unreportedRevenueVal'],
      })
    }

    if (filters.estimatedRevenueVal != null) {
      const op = COMPARISON_LABELS[filters.estimatedRevenueOp || 'gte']
      result.push({
        key: 'estimatedRevenue',
        label: `Estimated Revenue ${op} $${filters.estimatedRevenueVal}`,
        clearKeys: ['estimatedRevenueOp', 'estimatedRevenueVal'],
      })
    }

    if (filters.activeListing != null) {
      result.push({
        key: 'activeListing',
        label: `Active Listing: ${filters.activeListing}+`,
        clearKeys: ['activeListing'],
      })
    }

    if (filters.tinStatus) {
      result.push({ key: 'tinStatus', label: `TIN Status: ${filters.tinStatus}`, clearKeys: ['tinStatus'] })
    }

    if (filters.taxpayerType?.length) {
      result.push({
        key: 'taxpayerType',
        label: `Taxpayer Type: ${filters.taxpayerType.join(', ')}`,
        clearKeys: ['taxpayerType'],
      })
    }

    if (filters.hostLocation?.length) {
      result.push({
        key: 'hostLocation',
        label: `Host Location: ${filters.hostLocation[0]}`,
        clearKeys: ['hostLocation'],
      })
    }

    if (filters.hostProfileFilter && filters.hostProfileFilter !== 'show_all') {
      result.push({
        key: 'hostProfileFilter',
        label: HOST_PROFILE_LABELS[filters.hostProfileFilter],
        clearKeys: ['hostProfileFilter'],
      })
    }

    if (searchValue) {
      result.push({ key: 'search', label: `Search: "${searchValue}"`, clearKeys: [] })
    }

    return result
  }, [filters, searchValue])

  if (chips.length === 0) return null

  const handleRemove = (chip: ActiveFilter) => {
    if (chip.key === 'search') {
      onSearch('', '')
      return
    }
    const updated = { ...filters }
    chip.clearKeys.forEach(k => {
      delete updated[k]
    })
    onFiltersChange(updated)
  }

  const handleClearAll = () => {
    onFiltersChange({})
    onSearch('', '')
  }

  return (
    <Flex wrap='wrap' gap={8} align='center' style={{ marginBottom: 12 }}>
      {chips.map(chip => (
        <Tag key={chip.key} closable onClose={() => handleRemove(chip)} style={{ margin: 0 }}>
          {chip.label}
        </Tag>
      ))}
      <Typography.Link onClick={handleClearAll} style={{ fontSize: 13 }}>
        Clear All
      </Typography.Link>
    </Flex>
  )
}

export default STRActiveFilters
